import {
  BlotterTable,
  Box,
  type CareOrder,
  type Column,
  type ColumnDef,
  Dialog,
  Divider,
  type FixingDetails,
  type FixingIndex,
  Flex,
  FormGroup,
  getDisplayNameOrNameLabel,
  HStack,
  Input,
  type MinimalSubscriptionResponse,
  POSITIVE_CELL_CLASSNAME,
  SearchSelect,
  type SizeColumnParams,
  Text,
  toBig,
  toBigWithDefault,
  useBlotterTable,
  useCallbackRef,
  type UseDisclosureWithContextReturn,
  useGetDefaultContextMenuItems,
  WARNING_CELL_MED_CLASSNAME,
} from '@talos/kyoko';
import type { CellClassRules, GetContextMenuItemsParams, GridOptions, MenuItemDef } from 'ag-grid-community';
import { values } from 'lodash-es';
import { useMemo, useState } from 'react';
import type { Observable } from 'rxjs';
import { useFixingIndices } from '../../../../providers/FixingIndexProvider';
import {
  CARE_ORDER_STATUS_AGG_FUNC,
  careOrderStatusAggFunc,
  getCareOrderBlotterEntityStatusColumn,
} from '../columns/CareOrderBlotterEntityStatusColumn';
import { useCareOrderBlotterAutoGroupColDef } from '../columns/useCareOrderBlotterAutoGroupColDef';
import { type CareOrderBlotterEntity, isCareOrderRow, isLegRow } from '../types';
import { useModifyFixingContextMenuItem } from '../useCareOrderMenu';
import { useValidateFixingDataObs } from './useValidateFixingDataObs';

type ValidateFixingDialogProps = UseDisclosureWithContextReturn<
  Observable<MinimalSubscriptionResponse<CareOrderBlotterEntity>>
>;

export const ValidateFixingDialog = ({
  context: careOrderBlotterEntitiesObs,
  ...dialogProps
}: ValidateFixingDialogProps) => {
  return (
    <Dialog
      {...dialogProps}
      title="Validate Fixing"
      width={1100}
      showClose
      showConfirm={false}
      cancelLabel="Close"
      closeOnClickOutside={false} // false so we don't close the validate fixing dialog when clicking in the modify fixing dialog, for example
    >
      {careOrderBlotterEntitiesObs && (
        <ValidateFixingDialogContent careOrderBlotterEntitiesObs={careOrderBlotterEntitiesObs} />
      )}
    </Dialog>
  );
};

export const ValidateFixingDialogContent = ({
  careOrderBlotterEntitiesObs,
}: {
  careOrderBlotterEntitiesObs: Observable<MinimalSubscriptionResponse<CareOrderBlotterEntity>>;
}) => {
  const { fixingIndicesList } = useFixingIndices();

  const [fixingIndex, setFixingIndex] = useState<FixingIndex>();
  const [fixing, setFixing] = useState<string>('');

  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const { getItem: getModifyFixingItem, dialogComponent: modifyFixingDialogComponent } =
    useModifyFixingContextMenuItem();
  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<CareOrderBlotterEntity>) => {
    const items: (MenuItemDef | 'separator')[] = getDefaultContextMenuItems(params);
    const modifyFixingItem = getModifyFixingItem(params);
    if (modifyFixingItem) {
      items.unshift('separator');
      items.unshift(modifyFixingItem);
    }
    return items;
  });

  const autoGroupColumnDef = useCareOrderBlotterAutoGroupColDef();
  const gridOptions: GridOptions<CareOrderBlotterEntity> = useMemo(
    () => ({
      autoGroupColumnDef,
      treeData: true,
      showOpenedGroup: true,
      getDataPath: item => item.dataPath,
      suppressAggFuncInHeader: true,
      aggFuncs: {
        [CARE_ORDER_STATUS_AGG_FUNC]: careOrderStatusAggFunc,
      },
      groupDefaultExpanded: -1, // all groups start fully expanded
      getContextMenuItems,
    }),
    [autoGroupColumnDef, getContextMenuItems]
  );

  const dataObservable = useValidateFixingDataObs({ careOrderBlotterEntitiesObs, fixingIndex });
  const columns = useColumns(fixingIndex?.Name, fixing);
  const blotterTable = useBlotterTable({
    dataObservable,
    columns,
    errorObservable: undefined,

    rowID: 'rowID' satisfies keyof CareOrderBlotterEntity,
    gridOptions,
  });

  return (
    <>
      <Flex flexDirection="column" h="500px" gap="spacing12" data-testid="validate-fixing-dialog-content">
        <Text justifySelf="flex-start" alignSelf="flex-start">
          Enter fixing details and manually validate against today&apos;s Care Orders below.
        </Text>
        <HStack gap="spacing8" w="100%" alignItems="flex-start" justifyContent="flex-start">
          <FormGroup label="Fixing Index" w="180px">
            <SearchSelect
              selection={fixingIndex}
              options={fixingIndicesList}
              showClear
              getLabel={getDisplayNameOrNameLabel}
              onChange={setFixingIndex}
              dataTestID="validate-fixing-fixing-index-selector"
            />
          </FormGroup>

          <FormGroup label="Fixing" w="180px">
            <Input
              value={fixing}
              onChange={e => setFixing(e.target.value)}
              data-testid="validate-fixing-fixing-input"
            />
          </FormGroup>
        </HStack>
        <Divider orientation="horizontal" />
        <Box flex="1">
          <BlotterTable {...blotterTable} background="backgroundModal" />
        </Box>
      </Flex>
      {modifyFixingDialogComponent}
    </>
  );
};

const useColumns = (index: string | undefined, fixing: string | undefined): Column[] => {
  const fixingCellClassRules: CellClassRules<CareOrderBlotterEntity, unknown> = useMemo(() => {
    const fixingBig = toBig(fixing);
    return {
      [POSITIVE_CELL_CLASSNAME]: params => {
        if (!fixingBig || !index) {
          return false;
        }

        if (!params.data || isCareOrderRow(params.data) || isLegRow(params.data)) {
          return false;
        }

        // Apply the positive class name if we are a match
        return fixingBig.eq(toBigWithDefault(getFixingForIndex(params.data.FixingDetails, index), 0));
      },
      [WARNING_CELL_MED_CLASSNAME]: params => {
        if (!fixingBig || !index) {
          return false;
        }

        if (!params.data || isCareOrderRow(params.data) || isLegRow(params.data)) {
          return false;
        }

        // Apply the warning class name if we are not matching
        return !fixingBig.eq(toBigWithDefault(getFixingForIndex(params.data.FixingDetails, index), 0));
      },
    };
  }, [index, fixing]);

  return useMemo(
    () =>
      [
        {
          type: 'security',
          field: 'Symbol',
          title: 'Symbol',
        },
        {
          type: 'side',
          field: 'tradedSideOrSide',
          title: 'Side',
        },
        {
          type: 'size',
          field: 'OrderQty',
          title: 'Order Qty',
          params: {
            currencyField: 'Currency',
          } satisfies SizeColumnParams<CareOrder>,
        },
        getCareOrderBlotterEntityStatusColumn({ initialPinned: undefined }),
        {
          type: 'date',
          field: 'SubmitTime',
          title: 'Submit Time',
          width: 140,
          initialSort: '-',
        },
        {
          type: 'text',
          field: 'Group',
          title: 'Group',
          width: 130,
        },
        {
          field: 'fixingIndices',
          title: 'Fixing Index',
          id: 'fixingIndices',
          type: 'fixingIndices',
          width: 100,
        },
        {
          field: 'FixingDetails',
          title: 'Fixing Price',
          type: 'fixingPrice',
          id: 'fixingPrice',
          cellClassRules: fixingCellClassRules,
        },
      ] satisfies ColumnDef<CareOrderBlotterEntity>[],
    [fixingCellClassRules]
  );
};

function getFixingForIndex(fixingDetails: FixingDetails | undefined, index: string): string | undefined {
  if (fixingDetails?.Index === index) {
    return fixingDetails.Fixing;
  }

  if (fixingDetails?.MultilegFixingDetails) {
    return values(fixingDetails.MultilegFixingDetails).find(value => value.Index === index)?.Fixing;
  }

  return undefined;
}
